import { sumTotalsForModeledVariables } from '@/dashboards/SpendSummary/helpers/sumTotalsForModeledVariables';
import { useRoiConfig } from '@/dashboards/SpendSummary/hooks/useRoiConfig';
import { useSpendSummaryContext } from '@/dashboards/SpendSummary/hooks/useSpendSummaryContext';
import { useSpendSummaryQuery } from '@/dashboards/SpendSummary/hooks/useSpendSummaryQuery';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { useMemo } from 'react';

const EMPTY_VARIABLES: string[] = [];

/** Raw variable names → the labels the table shows, alphabetically. */
const toDisplayNames = (
    variables: string[],
    variableNameMap: Record<string, string>,
) =>
    variables
        .map((variable) => variableNameMap[variable] ?? variable)
        .sort((a, b) => a.localeCompare(b));

/**
 * The Total Media ROI KPI, and which variables it was computed over.
 *
 * When the dashboard's `modeledVariablesOnlyInTotalMediaRoi` setting is on, the
 * KPI is computed over only those variables that have both spend and
 * contribution in the selected period — variables with spend but no
 * contribution inflate the denominator without contributing to the numerator.
 *
 * Scoped to the KPI alone: the table, its totals row and the charts keep using
 * `spendSummaryData` unchanged, so the table footer ROI can legitimately differ
 * from this value. `includedVariables` / `spendWithoutContribution` exist so the
 * KPI can explain that difference rather than leave the viewer to find it.
 *
 * Both lists are empty when the setting is off — the KPI then covers everything
 * the table shows, so there is nothing to attribute.
 */
export const useTotalMediaRoi = () => {
    const {
        dashboard: { modeledVariablesOnlyInTotalMediaRoi },
        variableNameMap,
    } = useDashboardGlobalContext();
    const { calculate } = useRoiConfig();
    const { spendSummaryData } = useSpendSummaryContext();
    const { queryResult } = useSpendSummaryQuery();

    const modeledOnly = useMemo(
        () =>
            modeledVariablesOnlyInTotalMediaRoi
                ? sumTotalsForModeledVariables(queryResult.data?.rows ?? [])
                : null,
        [modeledVariablesOnlyInTotalMediaRoi, queryResult.data],
    );

    const totals = modeledOnly ?? spendSummaryData;

    const totalRoi = useMemo(
        () =>
            calculate({
                spend: totals.totalSpend,
                contribution: totals.totalContribution,
                contributionCostProduct: totals.totalContributionProduct,
            }),
        [calculate, totals],
    );

    const includedVariables = useMemo(
        () =>
            modeledOnly
                ? toDisplayNames(modeledOnly.includedVariables, variableNameMap)
                : EMPTY_VARIABLES,
        [modeledOnly, variableNameMap],
    );

    const spendWithoutContribution = useMemo(
        () =>
            modeledOnly
                ? toDisplayNames(
                      modeledOnly.spendWithoutContribution,
                      variableNameMap,
                  )
                : EMPTY_VARIABLES,
        [modeledOnly, variableNameMap],
    );

    return {
        totalRoi,
        isModeledVariablesOnly: modeledVariablesOnlyInTotalMediaRoi,
        includedVariables,
        spendWithoutContribution,
    };
};
